import { invariant } from "./invariant.js";
import { parseFunc } from "./parseFunc.js";
import { requireNamespace } from "./requireNamespace.js";

export type ExposeOptions = {
	/**
	 * Whether we should overwrite any existing values without warning.
	 */
	overwrite?: boolean;
};

/**
 * Expose a value to a potentially deeply nested global variable
 * @param name {string | object} Either a dot-separated path to the global variable, or an object to expose the value on
 * @param value {object | Function | Function[]} The value(s) to expose
 */
export function expose(
	name: object | string,
	value: object | ((...args: any[]) => any) | ((...args: any[]) => any)[] | string | number,
	options: ExposeOptions = {},
): void {
	if (Array.isArray(value)) {
		for (let func of value) {
			const { name: funcName } = parseFunc(func);

			invariant(
				funcName,
				"Unable to detect name of function! Name it, or only supply one function and include name in first parameter.",
			);
			expose(name, func); // Expose func on namespace found in pName
		}
	} else {
		let keyName: string | undefined;
		let namespace: any;

		if (typeof value === "function") {
			keyName = parseFunc(value as (...args: any[]) => any).name;
		}

		if (typeof name === "string") {
			let pathParts = name.split(".");

			if (!keyName) {
				keyName = pathParts[pathParts.length - 1];
				pathParts = pathParts.slice(0, pathParts.length - 1);
			}

			namespace = requireNamespace(window, pathParts);
		} else if (keyName && (typeof name === "object" || typeof name === "function")) {
			namespace = name;
		} else {
			invariant(false, "Invalid typeof parameter 'name', or unable to parse pValue!");
		}

		if (Object.hasOwn(namespace, keyName) && options?.overwrite !== true) {
			// If class is already defined, warn or show an alert
			console.warn(`${[name, keyName].join(".")} is already defined!`);
		} else {
			namespace[keyName] = value;
		}
	}
}
